import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { DataTable } from '@/components/DataTable'
import { LocalDate } from '@/components/LocalDate'
import { PageHeader } from '@/components/PageHeader'
import { PendingButton } from '@/components/PendingButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  type AdminAnnouncementRow,
  adminDeleteAnnouncement,
  adminListAnnouncements,
  adminSaveAnnouncement,
} from '@/lib/cms/admin/server'
import { ANNOUNCEMENT_LEVELS, isAnnouncementLive } from '@/lib/cms/cms'

export const Route = createFileRoute('/_authenticated/admin/announcements')({
  component: AdminAnnouncementsPage,
})

const QUERY_KEY = ['admin-announcements'] as const

const LEVEL_LABELS = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
} as const

/** ISO instant → value for a datetime-local input, in the admin's local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** datetime-local value (admin's local time) → ISO instant for the server. */
function toInstant(local: string): string | null {
  return local ? new Date(local).toISOString() : null
}

function LiveBadge({ row }: { row: AdminAnnouncementRow }) {
  const live = isAnnouncementLive(row, new Date())
  if (live) return <Badge>Live</Badge>
  if (!row.active) return <Badge variant="secondary">Off</Badge>
  return <Badge variant="outline">Scheduled</Badge>
}

function AnnouncementDialog({
  row,
  onClose,
}: {
  /** null = creating. */
  row: AdminAnnouncementRow | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [message, setMessage] = useState(row?.message ?? '')
  const [level, setLevel] = useState(row?.level ?? 'info')
  const [linkHref, setLinkHref] = useState(row?.linkHref ?? '')
  const [linkLabel, setLinkLabel] = useState(row?.linkLabel ?? '')
  const [active, setActive] = useState(row?.active ?? true)
  const [dismissible, setDismissible] = useState(row?.dismissible ?? true)
  const [startsAt, setStartsAt] = useState(toLocalInput(row?.startsAt ?? null))
  const [endsAt, setEndsAt] = useState(toLocalInput(row?.endsAt ?? null))

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const save = useMutation({
    mutationFn: () =>
      adminSaveAnnouncement({
        data: {
          ...(row ? { id: row.id } : {}),
          message,
          level,
          linkHref,
          linkLabel,
          active,
          dismissible,
          startsAt: toInstant(startsAt),
          endsAt: toInstant(endsAt),
        },
      }),
    onSuccess: () => {
      void invalidate()
      toast.success('Saved')
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't save", { description: (e as Error).message }),
  })

  const remove = useMutation({
    mutationFn: () => adminDeleteAnnouncement({ data: { id: row?.id ?? '' } }),
    onSuccess: () => {
      void invalidate()
      toast.success('Deleted')
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't delete", { description: (e as Error).message }),
  })

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {row ? 'Edit announcement' : 'New announcement'}
        </DialogTitle>
        <DialogDescription>
          Shown as a banner across the customer dashboard while live. Also
          served on the public content API (/api/content/announcements) — the
          text is world-readable, so nothing sensitive.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="ann-message">Message</Label>
          <Input
            id="ann-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Maintenance Saturday 02:00–03:00 UTC."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="ann-level">Level</Label>
            <Select
              value={level}
              onValueChange={(v) => setLevel(v as typeof level)}
            >
              <SelectTrigger id="ann-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANNOUNCEMENT_LEVELS.map((l) => (
                  <SelectItem key={l} value={l}>
                    {LEVEL_LABELS[l]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ann-link">Link (optional)</Label>
            <Input
              id="ann-link"
              value={linkHref}
              onChange={(e) => setLinkHref(e.target.value)}
              placeholder="/blog/maintenance or https://…"
            />
          </div>
        </div>

        {linkHref ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor="ann-link-label">Link label</Label>
            <Input
              id="ann-link-label"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="Read more"
            />
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="ann-starts">Starts (optional)</Label>
            <Input
              id="ann-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ann-ends">Ends (optional)</Label>
            <Input
              id="ann-ends"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label htmlFor="ann-active">Active</Label>
            <p className="text-xs text-muted-foreground">
              The hand switch; the start/end window applies on top.
            </p>
          </div>
          <Switch
            id="ann-active"
            checked={active}
            onCheckedChange={setActive}
          />
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label htmlFor="ann-dismissible">Dismissible</Label>
            <p className="text-xs text-muted-foreground">
              Off for incidents that must stay visible.
            </p>
          </div>
          <Switch
            id="ann-dismissible"
            checked={dismissible}
            onCheckedChange={setDismissible}
          />
        </div>

        <div className="flex items-center gap-2">
          <PendingButton
            pending={save.isPending}
            onClick={() => save.mutate()}
            disabled={!message}
          >
            Save
          </PendingButton>
          {row ? (
            <PendingButton
              variant="ghost"
              className="ml-auto text-destructive"
              pending={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete
            </PendingButton>
          ) : null}
        </div>
      </div>
    </DialogContent>
  )
}

/** LocalDate (not bare toLocaleString) so the SSR pass and the client agree
 *  on the first-paint text — see LocalDate's header. */
function WindowCell({ row }: { row: AdminAnnouncementRow }) {
  if (row.startsAt && row.endsAt) {
    return (
      <>
        <LocalDate iso={row.startsAt} withTime /> →{' '}
        <LocalDate iso={row.endsAt} withTime />
      </>
    )
  }
  if (row.startsAt) {
    return (
      <>
        from <LocalDate iso={row.startsAt} withTime />
      </>
    )
  }
  if (row.endsAt) {
    return (
      <>
        until <LocalDate iso={row.endsAt} withTime />
      </>
    )
  }
  return <>always</>
}

function AdminAnnouncementsPage() {
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminListAnnouncements(),
  })
  // 'new' | row-id | null — which dialog is open.
  const [editing, setEditing] = useState<string | null>(null)

  const columns: ColumnDef<AdminAnnouncementRow>[] = [
    {
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => (
        <button
          type="button"
          className="max-w-md truncate text-left font-medium hover:underline"
          onClick={() => setEditing(row.original.id)}
        >
          {row.original.message}
        </button>
      ),
    },
    {
      accessorKey: 'level',
      header: 'Level',
      cell: ({ row }) => (
        <Badge
          variant={row.original.level === 'info' ? 'outline' : 'destructive'}
        >
          {LEVEL_LABELS[row.original.level]}
        </Badge>
      ),
    },
    {
      id: 'window',
      header: 'Window',
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          <WindowCell row={row.original} />
        </span>
      ),
    },
    {
      id: 'live',
      header: 'State',
      cell: ({ row }) => <LiveBadge row={row.original} />,
    },
  ]

  const editingRow =
    editing && editing !== 'new'
      ? (query.data?.find((r) => r.id === editing) ?? null)
      : null

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Announcements"
        description="Banner notices shown across the customer dashboard."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus data-icon="inline-start" />
            New
          </Button>
        }
      />
      <DataTable
        columns={columns}
        data={query.data}
        isPending={query.isPending}
        emptyMessage="No announcements yet."
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        {editing !== null ? (
          <AnnouncementDialog
            key={editing}
            row={editingRow}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
    </div>
  )
}
